export interface VideoFileIdentity {
  name: string
  size: number
  lastModified: number
  origin?: 'capture' | 'gallery'
  sampleSha256?: string
  contentType?: string
}

const SAMPLE_BYTES = 64 * 1024

async function readSample(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => reader.result instanceof ArrayBuffer ? resolve(reader.result) : reject(new Error('VIDEO_SAMPLE_UNAVAILABLE'))
    reader.onerror = () => reject(new Error('VIDEO_SAMPLE_UNAVAILABLE'))
    reader.readAsArrayBuffer(blob)
  })
}

/** Mistake detection only: first/last 64KiB + size do not prove equality of every byte. */
export async function videoSampleSha256(file: File): Promise<string> {
  const [first, last] = await Promise.all([
    readSample(file.slice(0, SAMPLE_BYTES)),
    readSample(file.slice(Math.max(0, file.size - SAMPLE_BYTES), file.size)),
  ])
  const size = new TextEncoder().encode(`${file.size}:`)
  const sample = new Uint8Array(size.byteLength + first.byteLength + last.byteLength)
  sample.set(size)
  sample.set(new Uint8Array(first), size.byteLength)
  sample.set(new Uint8Array(last), size.byteLength + first.byteLength)
  const digest = await crypto.subtle.digest('SHA-256', sample)
  return [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('')
}

export async function createVideoFileIdentity(file: File, captured: boolean): Promise<VideoFileIdentity> {
  return {
    name: file.name, size: file.size, lastModified: file.lastModified,
    origin: captured ? 'capture' : 'gallery',
    contentType: file.type,
    ...(captured ? { sampleSha256: await videoSampleSha256(file) } : {}),
  }
}

export async function matchesVideoFileIdentity(saved: VideoFileIdentity | null, file: File): Promise<boolean> {
  if (!saved || saved.size !== file.size) return false
  if (saved.origin === 'capture' && saved.sampleSha256) return saved.sampleSha256 === await videoSampleSha256(file)
  // Gallery originals and older stored metadata retain the original strict comparison.
  return saved.name === file.name && saved.lastModified === file.lastModified
}

/** Only call after identity verification; Blob wrapping does not read the full recording. */
export function restoreCapturedUploadFile(saved: VideoFileIdentity, selected: File): File {
  if (saved.origin !== 'capture' || !saved.sampleSha256) return selected
  return new File([selected], saved.name, { type: saved.contentType ?? selected.type, lastModified: saved.lastModified })
}
